import React from 'react';
import { HeroSlideshow } from '../components/HeroSlideshow';
import { ParallaxSection } from '../components/ParallaxSection';
import { SectionHeading } from '../components/SectionHeading';
import { ShieldCheck, Award, Lightbulb, Clock, HardHat, Zap, Ruler, Briefcase, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
const features = [{
  icon: ShieldCheck,
  title: 'Uncompromising Safety',
  description: 'Safety is not just a policy, it is our culture. We maintain the highest standards to protect our team and your project.'
}, {
  icon: Award,
  title: 'Superior Quality',
  description: 'We deliver premium craftsmanship in every detail, ensuring your facility stands out and stands the test of time.'
}, {
  icon: Lightbulb,
  title: 'Innovative Solutions',
  description: 'Leveraging cutting-edge technology and modern engineering practices to solve complex construction challenges.'
}, {
  icon: Clock,
  title: 'Reliable Delivery',
  description: 'We respect your timeline. Our project management expertise ensures on-time completion without cutting corners.'
}];
const services = [{
  icon: HardHat,
  title: 'Commercial Construction',
  description: 'Full-scale construction services for office buildings, retail centers, and industrial facilities.'
}, {
  icon: Ruler,
  title: 'Mechanical Engineering',
  description: 'Custom HVAC and mechanical system design to optimize building performance and efficiency.'
}, {
  icon: Zap,
  title: 'Electrical Systems',
  description: 'Comprehensive electrical planning, installation, and maintenance for complex commercial needs.'
}, {
  icon: Briefcase,
  title: 'Project Management',
  description: 'End-to-end oversight ensuring your project stays on budget, on schedule, and up to spec.'
}];
export function HomePage() {
  return <div className="bg-white" data-id="element-247" data-mp="2rg0dg1auswyg">
      <HeroSlideshow data-id="element-248" data-mp="2rf8imsp2z398" />

      {/* Why Choose Us */}
      <section className="py-20 px-4 md:px-8 bg-white" data-id="element-249" data-mp="2mi8bsfkepwh2">
        <div className="max-w-7xl mx-auto" data-id="element-250" data-mp="2mhgods0yr51v">
          <SectionHeading title="Why Choose Sinclair" subtitle="We build more than structures; we build trust through excellence and integrity." data-id="element-251" data-mp="31fivgc77e8nv" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-252" data-mp="28qbp4r1ices2">
            {features.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="p-8 bg-gray-50 hover:bg-white hover:shadow-xl transition-all duration-300 border-b-4 border-transparent hover:border-[#C5A55A] group" data-id="element-253" data-mp="23sa4w2sci9f0" data-mpi={index}>
                <div className="w-14 h-14 bg-black text-[#C5A55A] flex items-center justify-center rounded-full mb-6 group-hover:bg-[#C5A55A] group-hover:text-black transition-colors" data-id="element-254" data-mp="5aq89ia143d1">
                  <feature.icon className="w-7 h-7" data-id="element-255" data-mp="3rpjb2y4ehhn3" />
                </div>
                <h3 className="text-xl font-bold font-serif mb-3 text-black" data-id="element-256" data-mp="j8wtnyxskavt">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-257" data-mp="o7hxe8c95cp0">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4 md:px-8 bg-gray-50" data-id="element-258" data-mp="2uyqt1uxwpfio">
        <div className="max-w-7xl mx-auto" data-id="element-259" data-mp="2ux5zo8nbw2me">
          <SectionHeading title="Our Expertise" subtitle="Comprehensive solutions for the modern built environment." data-id="element-260" data-mp="2z0cbdvt7mm09" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-261" data-mp="3f91s10lj03eq">
            {services.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: index % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="flex items-start gap-6 p-8 bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-262" data-mp="3aensclztza49" data-mpi={index}>
                <div className="shrink-0 text-[#C5A55A]" data-id="element-263" data-mp="3k5iii1g76tuz">
                  <service.icon className="w-10 h-10" data-id="element-264" data-mp="2ct03qn9m9w3a" />
                </div>
                <div data-id="element-265" data-mp="2cqvtlnl4k0uk">
                  <h3 className="text-xl font-bold font-serif mb-2 text-black" data-id="element-266" data-mp="28rmzuiq342z0">
                    {service.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-267" data-mp="oankur1y5moy">{service.description}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-4 md:px-8 bg-black text-white" data-id="element-268" data-mp="x9912fjy4pmk">
        <div className="max-w-4xl mx-auto text-center" data-id="element-269" data-mp="121alwpv6joo7">
          <Quote className="w-16 h-16 text-[#C5A55A] mx-auto mb-8 opacity-50" data-id="element-270" data-mp="122u0mc16trnv" />
          <blockquote className="text-2xl md:text-3xl font-serif italic leading-relaxed mb-8 text-gray-200" data-id="element-271" data-mp="16zc2931819vz">
            "Sinclair Commercial Division didn't just build our headquarters;
            they created a landmark. Their attention to detail and commitment to
            excellence is unmatched in the industry."
          </blockquote>
          <div className="text-[#C5A55A] font-bold text-lg tracking-widest uppercase" data-id="element-272" data-mp="3hrwuxg82x07v">
            Jon Engh
          </div>
          <div className="text-gray-500 text-sm mt-1" data-id="element-273" data-mp="3mq6oxni2hurw">
            Senior Vice President, First Horizon Bank
          </div>
        </div>
      </section>

      {/* CTA Parallax */}
      <ParallaxSection backgroundImage="https://images.unsplash.com/photo-1590274853856-f22d5ee3d228?w=1920&q=80" height="h-[60vh]" data-id="element-274" data-mp="oxoha2w25mlb">
        <div className="text-center" data-id="element-275" data-mp="12yhfaxlkizt6">
          <h2 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6" data-id="element-276" data-mp="xyctqt5szew3">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto" data-id="element-277" data-mp="1cu2j91a76a5k">
            Partner with Sinclair for world-class engineering and construction
            services.
          </p>
          <Link to="/contact" className="inline-block bg-[#C5A55A] hover:bg-[#D4AF37] text-black px-10 py-4 text-lg font-bold rounded-sm transition-colors" data-id="element-278" data-mp="1lmmdlpjghskl">
            Get a Consultation
          </Link>
        </div>
      </ParallaxSection>
    </div>;
}